import type { Control, FieldArrayWithId } from "react-hook-form";

import { Button, RepeatablePeopleSection, TextInput } from "@/shared/components";
import DateInput from "@/shared/components/DateInput";
import { TravelDetailsFormValues } from "../../redux/type";

type TravelTravellersSectionProps = {
  control: Control<TravelDetailsFormValues>;
  fields: FieldArrayWithId<TravelDetailsFormValues, "travellers", "id">[];
  coverType: TravelDetailsFormValues["coverType"];
  todayIso: string;
  canAddTraveller: boolean;
  additionalTravellersLayout?: "stacked" | "grid";
  additionalTravellerLimit: number;
  onAddTraveller: () => void;
  onRemoveTraveller: (index: number) => void;
  actionButtonsClassName?: string;
};

export function TravelTravellersSection({
  control,
  fields,
  coverType,
  todayIso,
  canAddTraveller,
  additionalTravellerLimit,
  onAddTraveller,
  onRemoveTraveller,
  additionalTravellersLayout,
  actionButtonsClassName,
}: TravelTravellersSectionProps) {
  const buttonWrapperClassName = actionButtonsClassName ?? "flex items-start gap-2 md:pt-7";

  return (
    <RepeatablePeopleSection
      title="Traveller Details"
      helperText={
        coverType !== "individual"
          ? `(You can add up to ${additionalTravellerLimit} additional travellers)`
          : null
      }
      additionalTravellersLayout={additionalTravellersLayout}
      additionalTravellersGridClassName="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
      fields={fields}
      canAddItem={canAddTraveller}
      onAddItem={onAddTraveller}
      onRemoveItem={onRemoveTraveller}
      renderItem={({
        field,
        index,
        isPrimary,
        canAddItem: canAdd,
        canRemoveItem,
        onAddItem,
        onRemoveItem,
      }) => (
        <div
          key={field.id}
          className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)_auto] md:items-start"
        >
          <TextInput
            control={control}
            name={`travellers.${index}.name`}
            label={isPrimary ? "Primary Traveller" : `Traveller ${index + 1}`}
            placeholder={isPrimary ? "Enter name" : `Traveller ${index + 1}`}
            required
          />

          <DateInput
            control={control}
            name={`travellers.${index}.dob`}
            label="Date of Birth"
            placeholder="DD/MM/YYYY"
            max={todayIso}
            showAge
            required
            yearOrder="desc"
          />

          <div className={buttonWrapperClassName}>
            {isPrimary && canAdd ? (
              <Button
                variant="secondary"
                className="flex h-9 w-9 items-center justify-center rounded-xl px-0 text-lg leading-none"
                aria-label="Add Traveller"
                onClick={onAddItem}
              >
                <span className="-mt-0.5">+</span>
              </Button>
            ) : null}

            {!isPrimary && canRemoveItem ? (
              <Button
                variant="secondary"
                className="flex h-9 w-9 items-center justify-center rounded-xl px-0 text-lg leading-none"
                aria-label={`Remove traveller ${index + 1}`}
                onClick={onRemoveItem}
              >
                <span className="-mt-0.5">-</span>
              </Button>
            ) : null}
          </div>
        </div>
      )}
    />
  );
}
